import type { Mode } from '../App'
import { ACTIONS } from './connectionUtils'

const AlertTime = {
  SAME_DAY_MORNING: 'SM',
  PREVIOUS_DAY_MORNING: 'PM',
  PREVIOUS_DAY_NIGHT: 'PN',
} as const

type AlertTime = (typeof AlertTime)[keyof typeof AlertTime]

interface recipe {
  id?: number
  name: string
  type: string | null
  tuppers: number
  alert: string | null
  alertTime: AlertTime | null
  desayuno: number
  almuerzo: number
  comida: number
  merienda: number
  cena: number
  time: number
  steps?: step[]
  ingredients?: recipeIngredient[]
  links?: recipeLink[]
  storage_qty?: number
}

interface step {
  id: number
  step_number: number
  step: string
  modified?: boolean
  new?: boolean
  deleted?: boolean
}

interface recipeIngredient {
  id: number
  productId: number
  productName: string
  quantity: number
  extra: string | null
  modified?: boolean
  new?: boolean
  deleted?: boolean
}

interface recipeLink {
  id: number
  url: string
  new?: boolean
  modified?: boolean
  deleted?: boolean
}

interface RecipeResponse {
  success: boolean
  recipes?: recipe[]
  error?: string
}

interface RecipeExtraDataResponse {
  success: boolean
  tuppers?: number
  steps?: step[]
  ingredients?: recipeIngredient[]
  links?: recipeLink[]
  error?: string
}

async function getRecipeList(user: string): Promise<RecipeResponse> {
  try {
    const response = await fetch('/api/recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.GET,
        user,
      }),
    })
    const data: RecipeResponse = await response.json()
    return data
  } catch (error) {
    console.error('Recipe error:', error)
    throw error
  }
}

async function getRecipeExtraData(
  user: string,
  recipeId: number
): Promise<RecipeExtraDataResponse> {
  try {
    const response = await fetch('/api/recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.GET_EXTRA,
        user,
        id: recipeId,
      }),
    })
    const data: RecipeExtraDataResponse = await response.json()
    return data
  } catch (error) {
    console.error('Recipe extra data error:', error)
    throw error
  }
}

async function insertRecipe(user: string, recipe: recipe) {
  try {
    const response = await fetch('/api/recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.INSERT,
        user,
        recipe,
      }),
    })
    return await response.json()
  } catch (error) {
    console.error('Recipe error:', error)
    throw error
  }
}

async function updateRecipe(user: string, recipe: recipe) {
  try {
    const response = await fetch('/api/recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.UPDATE,
        user,
        recipe,
      }),
    })
    return await response.json()
  } catch (error) {
    console.error('Recipe error:', error)
    throw error
  }
}

async function deleteRecipe(user: string, id: number) {
  try {
    const response = await fetch('/api/recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.DELETE,
        user,
        id,
      }),
    })
    return await response.json()
  } catch (error) {
    console.error('Recipe error:', error)
    throw error
  }
}

async function duplicateRecipe(user: string, id: number) {
  try {
    const response = await fetch('/api/recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.DUPLICATE,
        user,
        id,
      }),
    })
    return await response.json()
  } catch (error) {
    console.error('Recipe error:', error)
    throw error
  }
}

const ingredientListChanged = (newIngredients: recipeIngredient[] = []) => {
  return newIngredients.find(ingredient => {
    return ingredient.modified || ingredient.new || ingredient.deleted
  })
}

const stepsListChanged = (newSteps: step[] = []) => {
  return newSteps.find(step => {
    return step.modified || step.new || step.deleted
  })
}

const linkListChanged = (newLinks: recipeLink[] = []) => {
  return newLinks.find(link => {
    return link.modified || link.new || link.deleted
  })
}

const recipeChanged = (originalRecipe: recipe, newRecipe: recipe) => {
  return (
    originalRecipe.name !== newRecipe.name ||
    originalRecipe.type !== newRecipe.type ||
    originalRecipe.time !== newRecipe.time ||
    originalRecipe.tuppers !== newRecipe.tuppers ||
    originalRecipe.alert !== newRecipe.alert ||
    originalRecipe.alertTime !== newRecipe.alertTime ||
    originalRecipe.desayuno !== newRecipe.desayuno ||
    originalRecipe.almuerzo !== newRecipe.almuerzo ||
    originalRecipe.comida !== newRecipe.comida ||
    originalRecipe.merienda !== newRecipe.merienda ||
    originalRecipe.cena !== newRecipe.cena ||
    stepsListChanged(newRecipe.steps) ||
    ingredientListChanged(newRecipe.ingredients) ||
    linkListChanged(newRecipe.links)
  )
}

const setRecipeMode = (
  user: string,
  recipe: recipe | null | undefined,
  ingredients: recipeIngredient[] | null,
  steps: step[] | null,
  setMode: ((mode: Mode) => void) | undefined
) => {
  if (recipe === null || recipe === undefined || !setMode) return

  if (ingredients && steps) {
    launchRecipeMode(recipe, ingredients, steps, setMode)
  } else {
    getRecipeExtraData(user, recipe.id!).then(data => {
      if (data.success && data.ingredients && data.steps) {
        launchRecipeMode(recipe, data.ingredients, data.steps, setMode)
      } else {
        console.error('Failed to fetch recipe extra data')
      }
    })
  }
}
const launchRecipeMode = (
  recipe: recipe,
  ingredients: recipeIngredient[],
  steps: step[] | null,
  setMode: (mode: Mode) => void
) => {
  setMode({
    name: 'recipe',
    recipe: { ...recipe, ingredients, steps },
  } as Mode)
}

export {
  getRecipeList,
  getRecipeExtraData,
  insertRecipe,
  updateRecipe,
  deleteRecipe,
  duplicateRecipe,
  recipeChanged,
  setRecipeMode,
  AlertTime,
}

export type { recipe, step, recipeIngredient, recipeLink }
